import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import CareRoom from "@/components/CareRoom";
import { PatientRecordsDialog } from "@/components/MedicalRecords";
import { Loader2, Upload, Trash2, Plus, Calendar, Video, ShieldCheck, ShieldAlert, UserCheck, FolderLock } from "lucide-react";

const fmt = (iso) => new Date(iso).toLocaleString("en-IN", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
const SECTIONS = [
  { id: "acute", title: "24/7 Virtual Acute Care" }, { id: "primary", title: "Primary Care & Longitudinal Health" },
  { id: "chronic", title: "Connected Chronic Care" }, { id: "expert", title: "Expert Medical Opinions" },
  { id: "mental", title: "Mental Health & Therapy" },
];

const toBase64 = (file) => new Promise((res, rej) => {
  const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
});

function FileField({ testId, label, attached, onFile }) {
  return (
    <div>
      <Label className="text-xs font-semibold text-slate-600">{label}</Label>
      <label className="mt-1.5 flex items-center gap-2 h-11 px-3 rounded-xl border border-dashed border-slate-300 cursor-pointer text-sm text-slate-500 hover:border-[#F59E0B]">
        <Upload className="w-4 h-4" /> {attached ? "File attached ✓" : "Upload file"}
        <input data-testid={testId} type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
      </label>
    </div>
  );
}

function ProfileForm({ initial, onSaved, isTherapist }) {
  const [f, setF] = useState({
    name: initial.name || "", section: isTherapist ? "mental" : "acute", specialty: "", qualification: "",
    experience: 0, languages: "", fee: isTherapist ? 800 : 500, bio: "", education: "",
    aadhaar_number: "", registration_number: "", photo: null, aadhaar_doc: null, certificate_doc: null,
    license_authority: isTherapist ? "RCI" : "", license_number: "", modalities: "", session_length_min: 45,
    ...(initial.profile || {}),
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const upload = async (k, file) => {
    if (!file) return;
    if (file.size > 2_500_000) { toast.error("File too large (max 2.5MB)"); return; }
    set(k, await toBase64(file));
    toast.success("Uploaded");
  };

  const save = async () => {
    if (!f.specialty || !f.qualification) { toast.error("Specialty and qualification are required"); return; }
    setBusy(true);
    try {
      const payload = {
        ...f, section: isTherapist ? "mental" : f.section,
        experience: Number(f.experience) || 0, fee: Number(f.fee) || 0,
        session_length_min: Number(f.session_length_min) || 45,
        languages: typeof f.languages === "string" ? f.languages.split(",").map((x) => x.trim()).filter(Boolean) : f.languages,
        modalities: typeof f.modalities === "string" ? f.modalities.split(",").map((x) => x.trim()).filter(Boolean) : f.modalities,
      };
      const { data } = await api.put("/provider/profile", payload);
      toast.success("Profile submitted for verification");
      onSaved(data.user);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-7">
      <h2 className="font-head text-2xl font-semibold text-[#041E26]">{isTherapist ? "Complete your therapist profile" : "Complete your provider profile"}</h2>
      <p className="text-slate-500 mt-1 mb-6">Submit your credentials for verification. {isTherapist ? "You'll be listed under Mental Health & Therapy." : "Patients only see verified providers."}</p>
      <div className="grid md:grid-cols-2 gap-4">
        <div><Label className="text-xs font-semibold text-slate-600">Full name</Label><Input data-testid="pf-name" value={f.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5 h-11 rounded-xl" /></div>
        <div>
          <Label className="text-xs font-semibold text-slate-600">Care section</Label>
          {isTherapist ? (
            <div className="mt-1.5 h-11 flex items-center px-3 rounded-xl border border-slate-200 bg-slate-50 text-sm text-[#041E26]">Mental Health & Therapy</div>
          ) : (
            <select data-testid="pf-section" value={f.section} onChange={(e) => set("section", e.target.value)} className="mt-1.5 h-11 w-full rounded-xl border border-slate-300 px-3 text-sm">
              {SECTIONS.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
            </select>
          )}
        </div>
        <div><Label className="text-xs font-semibold text-slate-600">{isTherapist ? "Title / Specialisation" : "Specialty"}</Label><Input data-testid="pf-specialty" value={f.specialty} onChange={(e) => set("specialty", e.target.value)} placeholder={isTherapist ? "e.g. Clinical Psychologist" : "e.g. General Physician"} className="mt-1.5 h-11 rounded-xl" /></div>
        <div><Label className="text-xs font-semibold text-slate-600">Qualification</Label><Input data-testid="pf-qualification" value={f.qualification} onChange={(e) => set("qualification", e.target.value)} placeholder={isTherapist ? "M.Phil / PhD Clinical Psychology" : "MBBS, MD"} className="mt-1.5 h-11 rounded-xl" /></div>
        <div><Label className="text-xs font-semibold text-slate-600">Experience (years)</Label><Input data-testid="pf-exp" type="number" value={f.experience} onChange={(e) => set("experience", e.target.value)} className="mt-1.5 h-11 rounded-xl" /></div>
        <div><Label className="text-xs font-semibold text-slate-600">{isTherapist ? "Session fee (₹)" : "Consultation fee (₹)"}</Label><Input data-testid="pf-fee" type="number" value={f.fee} onChange={(e) => set("fee", e.target.value)} className="mt-1.5 h-11 rounded-xl" /></div>
        <div><Label className="text-xs font-semibold text-slate-600">Languages (comma separated)</Label><Input data-testid="pf-languages" value={Array.isArray(f.languages) ? f.languages.join(", ") : f.languages} onChange={(e) => set("languages", e.target.value)} placeholder="English, Hindi" className="mt-1.5 h-11 rounded-xl" /></div>
        <div><Label className="text-xs font-semibold text-slate-600">Education background</Label><Input data-testid="pf-education" value={f.education} onChange={(e) => set("education", e.target.value)} placeholder={isTherapist ? "NIMHANS Bengaluru" : "AIIMS Delhi"} className="mt-1.5 h-11 rounded-xl" /></div>
        {isTherapist ? (
          <>
            <div><Label className="text-xs font-semibold text-slate-600">Licensing authority</Label><Input data-testid="pf-license-auth" value={f.license_authority} onChange={(e) => set("license_authority", e.target.value)} placeholder="RCI / State Psychology board" className="mt-1.5 h-11 rounded-xl" /></div>
            <div><Label className="text-xs font-semibold text-slate-600">License / registration number</Label><Input data-testid="pf-license-no" value={f.license_number} onChange={(e) => set("license_number", e.target.value)} placeholder="e.g. RCI-A12345" className="mt-1.5 h-11 rounded-xl" /></div>
            <div><Label className="text-xs font-semibold text-slate-600">Therapy modalities (comma separated)</Label><Input data-testid="pf-modalities" value={Array.isArray(f.modalities) ? f.modalities.join(", ") : f.modalities} onChange={(e) => set("modalities", e.target.value)} placeholder="CBT, DBT, Mindfulness" className="mt-1.5 h-11 rounded-xl" /></div>
            <div><Label className="text-xs font-semibold text-slate-600">Session length (min)</Label><Input data-testid="pf-session-len" type="number" value={f.session_length_min} onChange={(e) => set("session_length_min", e.target.value)} className="mt-1.5 h-11 rounded-xl" /></div>
          </>
        ) : (
          <div><Label className="text-xs font-semibold text-slate-600">Medical registration no.</Label><Input data-testid="pf-reg" value={f.registration_number} onChange={(e) => set("registration_number", e.target.value)} placeholder="Medical Council Reg." className="mt-1.5 h-11 rounded-xl" /></div>
        )}
        <div><Label className="text-xs font-semibold text-slate-600">Aadhaar number</Label><Input data-testid="pf-aadhaar" value={f.aadhaar_number} onChange={(e) => set("aadhaar_number", e.target.value)} placeholder="XXXX-XXXX-XXXX" className="mt-1.5 h-11 rounded-xl" /></div>
        <FileField testId="upload-photo" label="Profile photo" attached={f.photo} onFile={(file) => upload("photo", file)} />
        <FileField testId="upload-aadhaar_doc" label="Aadhaar card" attached={f.aadhaar_doc} onFile={(file) => upload("aadhaar_doc", file)} />
        <FileField testId="upload-certificate_doc" label={isTherapist ? "License / degree certificate" : "Degree / certificate"} attached={f.certificate_doc} onFile={(file) => upload("certificate_doc", file)} />
      </div>
      <div className="mt-4"><Label className="text-xs font-semibold text-slate-600">Short bio</Label><textarea data-testid="pf-bio" value={f.bio} onChange={(e) => set("bio", e.target.value)} rows={3} className="mt-1.5 w-full rounded-xl border border-slate-300 p-3 text-sm" /></div>
      <Button data-testid="save-profile-btn" onClick={save} disabled={busy} className="mt-6 h-12 px-8 rounded-full bg-[#F59E0B] hover:bg-[#D97706] text-[#041E26] font-bold gap-2">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserCheck className="w-4 h-4" /> Submit for verification</>}
      </Button>
    </div>
  );
}

export default function DoctorPortal() {
  const { user } = useAuth();
  const isTherapist = user.role === "therapist";
  const [profile, setProfile] = useState(null);
  const [slots, setSlots] = useState([]);
  const [appts, setAppts] = useState([]);
  const [newSlot, setNewSlot] = useState("");
  const [room, setRoom] = useState(null);
  const [viewRecords, setViewRecords] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const p = await api.get("/provider/profile");
    setProfile(p.data);
    if (p.data.profile_complete) {
      const [s, a] = await Promise.all([api.get("/provider/slots"), api.get("/appointments")]);
      setSlots(s.data.slots); setAppts(a.data.appointments);
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const addSlot = async () => {
    if (!newSlot) { toast.error("Pick a date & time"); return; }
    const iso = new Date(newSlot).toISOString();
    if (new Date(iso) <= new Date()) { toast.error("Slot must be in the future"); return; }
    try { await api.post("/provider/slots", { start_time: iso, duration_min: 30 }); toast.success("Slot added"); setNewSlot(""); load(); }
    catch { toast.error("Could not add slot"); }
  };
  const delSlot = async (id) => { try { await api.delete(`/provider/slots/${id}`); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Cannot delete"); } };
  const admit = async (a) => { try { await api.post(`/appointments/${a.id}/admit`); toast.success("Patient admitted"); load(); setRoom(a); } catch { toast.error("Could not admit"); } };

  if (loading) return <div className="text-slate-400 py-10">Loading…</div>;
  if (!profile.profile_complete) return <ProfileForm initial={profile} isTherapist={isTherapist} onSaved={() => load()} />;

  return (
    <div>
      <div className={`rounded-2xl p-5 mb-8 flex items-center gap-3 ${profile.verified ? "bg-green-50 border border-green-200" : "bg-amber-50 border border-amber-200"}`}>
        {profile.verified ? <ShieldCheck className="w-6 h-6 text-green-600" /> : <ShieldAlert className="w-6 h-6 text-[#D97706]" />}
        <div>
          <div className="font-semibold text-[#041E26]">{profile.verified ? "Your profile is verified" : "Verification pending"}</div>
          <div className="text-sm text-slate-500">{profile.verified ? "You now appear in the patient directory." : "An enterprise admin will review your credentials shortly."}</div>
        </div>
      </div>

      {/* Availability */}
      <section className="mb-12">
        <h2 className="font-head text-2xl font-semibold text-[#041E26] mb-4">Manage availability</h2>
        <div className="flex flex-wrap items-end gap-3 mb-5">
          <div>
            <Label className="text-xs font-semibold text-slate-600">Add a slot</Label>
            <Input data-testid="new-slot-input" type="datetime-local" value={newSlot} onChange={(e) => setNewSlot(e.target.value)} className="mt-1.5 h-11 rounded-xl w-64" />
          </div>
          <Button data-testid="add-slot-btn" onClick={addSlot} className="h-11 rounded-full bg-[#041E26] hover:bg-[#0A2F3C] text-white gap-2"><Plus className="w-4 h-4" /> Add slot</Button>
        </div>
        {slots.length === 0 ? <p className="text-slate-400 text-sm">No slots yet.</p> : (
          <div className="flex flex-wrap gap-2">
            {slots.map((s) => (
              <div key={s.id} data-testid={`slot-chip-${s.id}`} className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm ${s.status === "booked" ? "bg-slate-100 border-slate-200 text-slate-400" : "bg-white border-slate-200 text-[#041E26]"}`}>
                <Calendar className="w-3.5 h-3.5" />{fmt(s.start_time)}
                {s.status === "booked" ? <Badge className="bg-amber-100 text-[#92400E] ml-1">booked</Badge>
                  : <button data-testid={`del-slot-${s.id}`} onClick={() => delSlot(s.id)} className="text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Appointments / queue */}
      <section>
        <h2 className="font-head text-2xl font-semibold text-[#041E26] mb-4">{isTherapist ? "Client queue" : "Patient queue"}</h2>
        {appts.length === 0 ? <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-400">No appointments yet.</div> : (
          <div className="space-y-3">
            {appts.map((a) => (
              <div key={a.id} data-testid={`docappt-${a.id}`} className="bg-white rounded-2xl border border-slate-200 p-5 flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <div className="font-semibold text-[#041E26]">{a.patient_name}</div>
                  <div className="text-sm text-slate-500 mt-0.5">{a.symptoms}</div>
                  <div className="text-xs text-slate-400 mt-1 flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{fmt(a.slot_time)}</div>
                </div>
                <div className="flex items-center gap-3">
                  <Button data-testid={`records-${a.id}`} onClick={() => setViewRecords(a)} variant="outline" className="rounded-full gap-2"><FolderLock className="w-4 h-4" /> Records</Button>
                  {a.status === "waiting" && <Badge className="bg-amber-100 text-[#92400E] animate-pulse">waiting to join</Badge>}
                  {a.status === "waiting" && (
                    <Button data-testid={`admit-${a.id}`} onClick={() => admit(a)} className="rounded-full bg-[#F59E0B] hover:bg-[#D97706] text-[#041E26] font-semibold gap-2"><UserCheck className="w-4 h-4" /> Admit</Button>
                  )}
                  {["admitted", "in_call"].includes(a.status) && (
                    <Button data-testid={`enter-${a.id}`} onClick={() => setRoom(a)} className="rounded-full bg-[#041E26] hover:bg-[#0A2F3C] text-white gap-2"><Video className="w-4 h-4" /> Enter room</Button>
                  )}
                  {["booked", "completed", "pending_payment", "cancelled"].includes(a.status) && <Badge className="bg-slate-100 text-slate-500">{a.status === "pending_payment" ? "awaiting payment" : a.status}</Badge>}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {room && <CareRoom appointment={room} otherName={room.patient_name} onClose={() => { setRoom(null); load(); }} />}
      {viewRecords && <PatientRecordsDialog patientId={viewRecords.patient_id} patientName={viewRecords.patient_name} onClose={() => setViewRecords(null)} />}
    </div>
  );
}
